import { useRef, useState, useEffect, MutableRefObject } from 'react'

type useDropdownReturn<Element extends HTMLElement> = [
  MutableRefObject<Element>,
  boolean,
  (_: boolean) => void
]

export function useHandleClickOutside<El extends HTMLElement>(
  open: boolean,
  onClick: () => void
): MutableRefObject<El> {
  const ref = useRef<El>(null)
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (
        open &&
        e.target instanceof HTMLElement &&
        !ref.current?.contains(e.target)
      ) {
        onClick()
      }
    }

    document.addEventListener('click', onClickOutside)

    return () => document.removeEventListener('click', onClickOutside)
  }, [ref, open, onClick])

  return ref
}

export function useDropdown<El extends HTMLElement>(): useDropdownReturn<El> {
  const [isOpen, setOpen] = useState(false)
  const ref = useHandleClickOutside<El>(isOpen, () => setOpen(false))

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (
        isOpen &&
        e.target instanceof HTMLElement &&
        !ref.current?.contains(e.target)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener('click', onClickOutside)

    return () => document.removeEventListener('click', onClickOutside)
  }, [ref, isOpen, setOpen])

  return [ref, isOpen, setOpen]
}
